import { cn } from '~/lib/utils';

type StatusType = 'active' | 'pending' | 'error' | 'inactive' | 'success' | 'warning';

interface StatusIndicatorProps {
  /** Status type determines color and animation */
  status: StatusType;
  /** Optional label text (displayed in uppercase monospace) */
  label?: string;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Whether to show the pulsing animation */
  pulse?: boolean;
}

// Semantic tokens instead of raw hex values, so the dot follows the
// light/dark theme swap automatically.
const statusDotClasses: Record<StatusType, string> = {
  active: 'bg-success',
  success: 'bg-success',
  pending: 'bg-warning',
  warning: 'bg-warning',
  error: 'bg-destructive',
  inactive: 'bg-muted-foreground-2',
};

const sizeConfig = {
  small: { dot: 'size-1.5', font: 'text-xs', gap: 'gap-2' },
  medium: { dot: 'size-2', font: 'text-sm', gap: 'gap-2.5' },
  large: { dot: 'size-2.5', font: 'text-base', gap: 'gap-3' },
};

export function StatusIndicator({
  status,
  label,
  size = 'medium',
  pulse = true,
}: StatusIndicatorProps) {
  const config = sizeConfig[size];
  const shouldPulse = pulse && (status === 'active' || status === 'success');

  return (
    <div className={cn('inline-flex items-center', config.gap)}>
      <span className="relative inline-flex shrink-0">
        {shouldPulse && (
          <span
            className={cn(
              'absolute inset-0 animate-ping rounded-full opacity-75',
              statusDotClasses[status]
            )}
          />
        )}
        <span
          className={cn('relative inline-block rounded-full', config.dot, statusDotClasses[status])}
        />
      </span>
      {label && (
        <span
          className={cn(
            'font-mono font-medium tracking-[0.05em] text-muted-foreground uppercase',
            config.font
          )}
        >
          {label}
        </span>
      )}
      <span className="sr-only">{label ? `${label}: ${status}` : status}</span>
    </div>
  );
}
